/**
 * token-store — the single home for staff session tokens (ADR 0052).
 *
 * Tokens live in localStorage (not cookies): the API returns them in the
 * response body and the static export has no server to set cookies. Same
 * pattern as the driver app's token-store (ADR 0016).
 *
 * refreshSession() is the ONLY refresh path in the app. Refresh tokens are
 * single-use on the OAuth server — two concurrent refreshes with the same
 * token race each other, and before this store the loser's 401 wiped the
 * whole session (the latent bug verified in the Phase 0 baseline, spec W5).
 * Every caller (api.ts 401 retry, auth-context proactive timer, AuthGate)
 * must share this one in-flight promise. (R9)
 */

import { coordinatedRefresh } from "./refresh-coordinator";
import { publishAuth } from "./auth-broadcast";

export const API_BASE = process.env.NEXT_PUBLIC_API_URL || "http://localhost:17003";

const ACCESS_KEY = "__web_at";
const REFRESH_KEY = "__web_rt";

// Pre-ADR-0052 sessions kept the access token in this cookie; clear it so a
// stale token can't shadow the store. (The old __session_refresh cookie was
// httpOnly — JS can't remove it; it just expires unused.)
const LEGACY_ACCESS_COOKIE = "__session";

let legacyCleaned = false;
function cleanupLegacyCookie() {
  if (legacyCleaned || typeof document === "undefined") return;
  legacyCleaned = true;
  if (document.cookie.includes(`${LEGACY_ACCESS_COOKIE}=`)) {
    document.cookie = `${LEGACY_ACCESS_COOKIE}=; path=/; max-age=0`;
  }
}

export function getAccessToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  cleanupLegacyCookie();
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  if (typeof localStorage === "undefined") return null;
  return localStorage.getItem(REFRESH_KEY);
}

export function setTokens(tokens: { access_token: string; refresh_token: string }): void {
  if (typeof localStorage === "undefined") return;
  localStorage.setItem(ACCESS_KEY, tokens.access_token);
  localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
}

/**
 * ADR 0228 D5 — นับรุ่นของ session เพื่อกัน refresh ที่เริ่มก่อน logout เขียนกลับมา
 *
 * ไม่มีตัวนี้ ผู้ใช้กด logout ขณะมี refresh ค้างอยู่ จะได้ token ใหม่เขียนทับหลังการล้าง
 * แล้ว "ล็อกอินอยู่" ต่อทั้งที่กดออกไปแล้ว
 */
let authGeneration = 0;

/**
 * ล้าง session — และบอกแท็บอื่นเสมอ (ADR 0228 D3)
 *
 * ผู้เรียกมีสองจุดและทั้งคู่คือ "session ตายจริง": refresh ได้ 401 ด้วย token ของเราเอง
 * (family ถูก revoke / หมดอายุ) กับการกด logout
 *
 * เดิมประกาศเฉพาะตอน logout ทำให้เมื่อ refresh ล้มเหลว แท็บอื่นกลายเป็น **แท็บผี** —
 * `localStorage` ว่างแล้ว (แชร์กัน) แต่หน้าจอยังเป็นแอปปกติ ผู้ใช้คลิกต่อได้และเพิ่งรู้ตัว
 * ตอนยิง request ถัดไปแล้วโดนเด้ง · ยืนยันด้วยการทดสอบจริง: อีกแท็บได้ `hasRT: false`
 * แต่ `stillShowsApp: true` และไม่ได้รับ broadcast ใด ๆ
 */
export function clearTokens(): void {
  authGeneration += 1;
  publishAuth({ type: "logout" });
  if (typeof localStorage === "undefined") return;
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  // กัน tenant ค้างข้าม logout — staff คนใหม่บนเครื่องเดิมอาจได้ tenant ผิดชั่วขณะ
  localStorage.removeItem("active_tenant_id");
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * ชั้นในแท็บ (ADR 0052 R9) + ชั้นข้ามแท็บ (ADR 0228) — ต้องมีทั้งคู่
 *
 * `refreshInFlight` กัน caller หลายตัวในแท็บเดียวกัน · `coordinatedRefresh` กันแท็บอื่น
 * ยิงด้วย token ใบเดียวกัน ซึ่งจะทำให้ OAuth ถือว่า family ถูกขโมยแล้ว revoke ทั้งชุด
 */
export function refreshSession(): Promise<boolean> {
  if (!refreshInFlight) {
    refreshInFlight = coordinatedRefresh({
      lockName: "web-auth-refresh",
      readToken: getRefreshToken,
      run: doRefresh,
    })
      // `skipped` = แท็บอื่นทำให้แล้ว storage มี token ที่ใช้ได้ → สำเร็จเหมือนกัน
      .then((r) => r !== "failed")
      .finally(() => {
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
}

/**
 * ยิง refresh จริง — รับ token มาเป็นพารามิเตอร์ ไม่อ่านเอง
 *
 * ตั้งใจไม่อ่านจาก storage ข้างใน: ผู้เรียก (`coordinatedRefresh`) อ่านไปแล้วหลังได้ล็อก
 * การอ่านซ้ำที่นี่เปิดช่องให้ได้คนละค่า ซึ่งเป็นบั๊กชนิดเดียวกับที่ ADR นี้แก้อยู่
 */
async function doRefresh(refreshToken: string): Promise<boolean> {
  const generationAtStart = authGeneration;
  try {
    const res = await fetch(`${API_BASE}/api/v1/staff-auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
    if (!res.ok) {
      /* ADR 0228 D4 — 401 ล้างเฉพาะเมื่อ token ใน storage ยังเป็นใบที่เราเพิ่งยื่นไป
         ถ้าเปลี่ยนไปแล้วแปลว่ามีแท็บอื่น refresh สำเร็จ การล้างจะไปฆ่า session ที่ยังดีอยู่ */
      if (res.status === 401 && getRefreshToken() === refreshToken) clearTokens();
      return false;
    }
    const data = (await res.json()) as { access_token?: string; refresh_token?: string };
    if (!data.access_token || !data.refresh_token) return false;
    // ADR 0228 D5 — logout เกิดขึ้นระหว่างที่ request นี้ค้างอยู่ → ทิ้งผลลัพธ์ ห้ามเขียน
    if (authGeneration !== generationAtStart) return false;
    setTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
    // ADR 0228 D3 — บอกแท็บอื่นให้ตั้งนาฬิกาใหม่ ไม่งั้นทุกแท็บยังตื่นพร้อมกันทุก 14 นาที
    publishAuth({ type: "refreshed", exp: expOf(data.access_token) });
    return true;
  } catch {
    // Network blip — keep the tokens, the next attempt may succeed.
    return false;
  }
}

/** Logout: revoke server-side (best effort), then drop the local session. */
export async function revokeSession(): Promise<void> {
  const refreshToken = getRefreshToken();
  /* ADR 0228 D6 — revoke **ก่อน** ล้าง local
     เดิมล้างก่อนแล้วค่อยยิงด้วย token ที่จับไว้ ถ้าระหว่างนั้นมีแท็บอื่น refresh สำเร็จ
     token ใบนั้นถูกหมุนไปแล้ว → revoke ล้มเหลวเงียบ ๆ (best effort) → **family ยังมี
     ชีวิตบนเซิร์ฟเวอร์** ผู้ใช้กด logout แล้วแต่ token ในแท็บอื่นยังใช้ได้
     ยิง revoke ด้วย token ที่ถูกหมุนแล้วปลอดภัย — endpoint นี้คือ revoke ไม่ใช่ refresh
     จึงไม่ไปกระตุ้น reuse detection */
  await revokeToken(refreshToken);
  const rotated = getRefreshToken();
  if (rotated && rotated !== refreshToken) await revokeToken(rotated);
  clearTokens(); // ประกาศ logout ให้แท็บอื่นอยู่ข้างใน
}

/** อ่าน `exp` จาก JWT แบบไม่ throw — ใช้บอกแท็บอื่นว่านาฬิการอบหน้าอยู่ตรงไหน */
function expOf(accessToken: string): number {
  try {
    const payload = JSON.parse(atob(accessToken.split(".")[1])) as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp : 0;
  } catch {
    return 0;
  }
}

async function revokeToken(refreshToken: string | null): Promise<void> {
  if (!refreshToken) return;
  try {
    await fetch(`${API_BASE}/api/v1/staff-auth/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  } catch {
    // Revoke is best effort — the local logout below happens either way.
  }
}
